import { API_DOMAIN } from '../config';

const CSRF_STORAGE_KEY = 'kryla.csrfToken';
const LOGIN_PATH = '/login';
let redirectingToLogin = false;

let csrfToken: string | null = null;
if (typeof window !== 'undefined') {
  csrfToken = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
}

/**
 * Read the cached CSRF token.
 */
export function getCsrfToken() {
  return csrfToken;
}

/**
 * Persist the CSRF token for future requests.
 *
 * @param token
 */
export function setCsrfToken(token: string | null) {
  csrfToken = token;
  if (typeof window === 'undefined') {
    return;
  }
  if (token) {
    window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  } else {
    window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
  }
}

const redirectToLogin = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname === LOGIN_PATH) {
    redirectingToLogin = false;
    return;
  }

  if (redirectingToLogin) {
    return;
  }

  redirectingToLogin = true;
  window.location.replace(LOGIN_PATH);
};

// `document.cookie = ''` is a no-op — clearing requires expiring each named
// cookie individually. Backend session/CSRF cookies live on the API origin
// (different from this SPA's origin in some setups), so this only handles
// admin-domain cookies as a defense-in-depth.
const clearAdminCookies = () => {
  if (typeof document === 'undefined') {
    return;
  }
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const name = c.split('=')[0]?.trim();
    if (!name) {
      continue;
    }
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
};

/**
 * Refresh the CSRF token from the backend. Throws with `err.status` set so
 * callers can differentiate the failure mode (401 vs transient).
 *
 * Note: `/api/csrf` is behind `apiAuthMiddleware` by design — it returns
 * 401 for anonymous visitors. That is NOT a bug. Login itself is exempt
 * from CSRF on the backend (POST /api/auth sits before csrfProtection).
 */
export async function refreshCsrfToken() {
  const response = await fetch(`${API_DOMAIN}/api/csrf`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = new Error(
      response.status === 401
        ? 'Unauthorized'
        : `CSRF refresh failed: ${response.status}`
    ) as ApiError;
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  const token = data?.csrfToken;
  if (token) {
    setCsrfToken(token);
  }
  return token;
}

/**
 * Ensure a CSRF token is available, optionally forcing refresh. Swallows
 * all errors — anonymous users hitting a CSRF-less public endpoint (e.g.
 * login) should proceed without a token. Dead-session detection happens
 * downstream when the actual request returns 401.
 *
 * @param forceRefresh
 */
export async function ensureCsrfToken(forceRefresh = false) {
  if (!forceRefresh && csrfToken) {
    return csrfToken;
  }
  try {
    await refreshCsrfToken();
  } catch {
    // Anonymous → 401 here is expected. Other failures fall through; the
    // caller will see a 403 from the eventual mutating request and the
    // top-level `request()` retry loop will surface that.
  }
  return csrfToken;
}

/**
 * Build a typed error from a non-OK response. Body is parsed best-effort
 * and attached as `err.response.data` so callers can read
 * `err.response?.data?.message` (matching the axios shape that pages and
 * `useFormErrors` already expect).
 */
async function buildHttpError(response: Response): Promise<ApiError> {
  let body: ParsedErrorBody | null = null;
  try {
    const text = await response.text();
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = { message: text };
      }
    }
  } catch {
    // ignore — body unreadable
  }
  const err = new Error(
    body?.message || body?.error || `HTTP error ${response.status}`
  ) as ApiError;
  err.status = response.status;
  err.response = { status: response.status, data: body };
  return err;
}

/**
 * Upload multipart form data with CSRF handling.
 *
 * @param url
 * @param formData
 * @param retryOnCsrfFailure
 */
export async function uploadFormData(
  url: string,
  formData: FormData,
  retryOnCsrfFailure = true
) {
  await ensureCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    setCsrfToken(null);
    clearAdminCookies();
    redirectToLogin();
    return response;
  }

  if (response.status === 403 && retryOnCsrfFailure) {
    try {
      await ensureCsrfToken(true);
    } catch {
      // 401 from refresh already redirected — fall through to return 403
    }
    return uploadFormData(url, formData, false);
  }

  return response;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

/** Error thrown by the API client, augmented with HTTP status and body. */
export interface ApiError extends Error {
  status?: number;
  response?: { status: number; data: unknown };
}

/** Best-effort parse of a non-OK response body. */
interface ParsedErrorBody {
  message?: string;
  error?: string;
}

export interface RequestOptions {
  signal?: AbortSignal;
  retryOnCsrfFailure?: boolean;
}

/**
 * Issue a JSON API request with CSRF handling. Pass `signal` via options
 * to support cancellation on rapid filter/route changes.
 *
 * @param method
 * @param path
 * @param data
 * @param options
 */
// `T` defaults to `any` so untyped callers keep working during the gradual
// typing migration; new/migrated callers pass an explicit response type.
export default async function request<T = any>(
  method: Method,
  path: string,
  data?: unknown,
  options: RequestOptions | boolean = {}
): Promise<T> {
  // Back-compat: a few callers passed `retryOnCsrfFailure` as a positional
  // boolean. Map both shapes onto the options object.
  const opts: RequestOptions =
    typeof options === 'boolean' ? { retryOnCsrfFailure: options } : options;
  const retryOnCsrfFailure = opts.retryOnCsrfFailure ?? true;
  const signal = opts.signal;

  const normalizedMethod = method.toString().toUpperCase();
  const requiresCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(normalizedMethod);

  if (requiresCsrf) {
    await ensureCsrfToken();
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const isBodyless = ['GET', 'HEAD', 'OPTIONS'].includes(normalizedMethod);
  let url = API_DOMAIN + `/${path}`.replace('//', '/');
  if (isBodyless && data !== undefined) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item !== undefined && item !== null) {
            params.append(k, String(item));
          }
        }
      } else {
        params.append(k, String(v));
      }
    }
    url += (url.includes('?') ? '&' : '?') + params.toString();
  }

  const response = await fetch(url, {
    method: normalizedMethod,
    headers,
    credentials: 'include',
    body: !isBodyless && data !== undefined ? JSON.stringify(data) : undefined,
    signal,
  });

  if (response.status === 403 && requiresCsrf && retryOnCsrfFailure) {
    try {
      await refreshCsrfToken();
      return request<T>(method, path, data, {
        ...opts,
        retryOnCsrfFailure: false,
      });
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 401) {
        // Session died between getting a 403 and trying to refresh.
        setCsrfToken(null);
        clearAdminCookies();
        redirectToLogin();
        apiErr.response = { status: 401, data: null };
        throw apiErr;
      }
      // fall through to error handling below
    }
  }

  if (response.status === 401) {
    setCsrfToken(null);
    clearAdminCookies();
    redirectToLogin();
    const err = new Error('Unauthorized') as ApiError;
    err.status = 401;
    err.response = { status: 401, data: null };
    throw err;
  }

  if (!response.ok) {
    throw await buildHttpError(response);
  }

  return response.json();
}
